import moment from 'moment-timezone';
import numeral from 'numeral';

const DATE_FORMAT = 'DD/MM/YYYY';
const NO_VALUE = '--';
const NUMBER_FORMAT = '0,0.00';
const PERCENT_FORMAT = '0.00';
const PRICE_FORMAT = '$0,0.00';
const TIME_FORMAT = 'h:mm:ss a';
const TIME_FORMAT_1 = 'h:mm a';
const PAY_FREQUENCY_BY_CODE = {
	[1]: 'Yearly',
	[2]: 'Half Yearly',
	[4]: 'Quarterly',
	[12]: 'Monthly'
};

const colorSequence = [
	'#0095C8',
	'#0C3B6C',
	'#89A0B3',
	'#215EAC',
	'#00A58E',
	'#38B249',
	'#AFDBCE',
	'#FFC649',
	'#EF4437',
	'#CDCBD5',
	'#02032F'
];

// set the locales
moment.locale('en-au');
numeral.locale('en');

// set the abbreviations
numeral.locales['en'].abbreviations = {
	thousand: 'K',
	million: 'M',
	billion: 'B',
	trillion: 'T'
};

function sanitizeNumber(val) {
	// This was added to get around a bug in numeral for numbers like 1.176083712526649e-14.
	if (val && val.toString().indexOf('e') > -1) {
		// It is a valid number but a very tiny one at that, just return 0.
		return 0;
	}

	return val;
}

const isValid = val => {
	if (val === undefined || val === -32768 || val === '-32768') {
		return false;
	}

	return true;
};

const isValidNumber = val => {
	return isValid(val) && !isNaN(val);
};

const isValidText = val => {
	return isValid(val) && val !== null && val !== '';
};

const date = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	options = options || {};

	const m = moment(val);

	return m.format(options.formatString || DATE_FORMAT);
};
const time = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	options = options || {};

	const m = moment(val);

	return m.format(options.formatString || TIME_FORMAT_1);
};
const timeMin = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	options = options || {};

	const m = moment(val);

	return m.format(options.formatString || TIME_FORMAT_1);
};
const utcDate = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	options = options || {};

	const m = moment.utc(val);

	return m.format(options.formatString || DATE_FORMAT);
};

// This implementaion of moment allows the moment lib to automaticly parse the date to the time zone that it detects
const dateRelativeDays = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	const m = moment(val);
	const today = moment();

	if (m.isSame(today, 'day')) {
		return 'Today';
	}

	const yesterday = moment().subtract(1, 'd');

	if (m.isSame(yesterday, 'day')) {
		return 'Yesterday';
	}

	const tomorrow = moment().add(1, 'd');

	if (m.isSame(tomorrow, 'day')) {
		return 'Tomorrow';
	}

	options = options || {};

	return m.format(options.formatString || DATE_FORMAT);
};

const utcDateRelativeDays = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	const mUtc = moment.utc(val);
	const todayUtc = moment.utc();

	if (mUtc.isSame(todayUtc, 'day')) {
		return 'Today';
	}

	const yesterdayUtc = moment.utc().subtract(1, 'd');

	if (mUtc.isSame(yesterdayUtc, 'day')) {
		return 'Yesterday';
	}

	const tomorrowUtc = moment.utc().add(1, 'd');

	if (mUtc.isSame(tomorrowUtc, 'day')) {
		return 'Tomorrow';
	}

	options = options || {};

	return mUtc.format(options.formatString || DATE_FORMAT);
};

const dateTime = (val, options) => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	options = options || {};

	const m = moment(val);

	return `${m.format(options.dateFormatString || DATE_FORMAT)} ${m.format(options.timeFormatString || TIME_FORMAT)}`;
};


const dateFromNow = val => {
	if (!isValid(val)) {
		return NO_VALUE;
	}

	const m = moment(val);

	return m.from(moment(), true);
};

const magnitude = (val, options) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	val = sanitizeNumber(val);

	options = options || {};

	const prependString = options.prependString || '';

	if (options.format === 'full') {
		return `${prependString}${numeral(val).format('0.00a', Math.floor).replace('B', ' Billion').replace('M', ' Million')}`;
	}

	return `${prependString}${numeral(val).format('0.00a', Math.floor)}`;
};

const number = (val, options) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	val = sanitizeNumber(val);

	options = options || {};

	if (options.showSign && val > 0) {
		options.formatString = `+${options.formatString || NUMBER_FORMAT}`;
	}

	return numeral(val).format(options.formatString || NUMBER_FORMAT, Math.floor);
};

const fileSize = (val, options) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	val = sanitizeNumber(val);

	return number(val, Object.assign({}, options, { formatString: '0.0b' }));
};

const payFrequency = val => {
	return PAY_FREQUENCY_BY_CODE[val] || NO_VALUE;
};

const percent = (val, options = {}) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	const formatString = options.formatString || PERCENT_FORMAT;

	options = Object.assign({}, options, { formatString: `${formatString}%` });

	return number(val / 100, options);
};

const price = (val, options) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	val = sanitizeNumber(val);

	const formatString = options && options.formatString
		? options.formatString
		: PRICE_FORMAT;

	return numeral(val.toFixed(4)).format(formatString, Math.floor);
};

const priceMagnitude = (val, options) => {
	if (!isValidNumber(val)) {
		return NO_VALUE;
	}

	val = sanitizeNumber(val);

	options = options || {};

	if (options.format === 'full') {
		return numeral(val).format('$0.00a').replace('B', ' Billion').replace('M', ' Million');
	}

	return numeral(val).format('$0.00a', Math.floor);
};

const priceRange = (valLow, valHigh, options) => {
	if (!isValidNumber(valLow) || !isValidNumber(valHigh)) {
		return NO_VALUE;
	}

	return `${price(valLow, options)} - ${price(valHigh, options)}`;
};
const text = val => {
	if (!isValidText(val)) {
		return NO_VALUE;
	}

	return val;
};

const textFirstLetterCapitalize = val => {
	if (!isValidText(val)) {
		return NO_VALUE;
	}

	return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
};

export default {
	NO_VALUE,
	colorSequence,
	date,
	time,
	timeMin,
	utcDate,
	dateFromNow,
	dateRelativeDays,
	utcDateRelativeDays,
	dateTime,
	fileSize,
	isValid,
	isValidText,
	magnitude,
	number,
	payFrequency,
	percent,
	price,
	priceMagnitude,
	priceRange,
	text,
	textFirstLetterCapitalize
};



// WEBPACK FOOTER //
// ./src/shared/utils/format.js